import React, { useEffect } from 'react';
import { Box, InputLabel, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { autoSubmissionSchema, useSubmitAutoSubmission } from '../api/auto-submission';
import { Stack } from '@mui/system';
import * as yup from 'yup';
import _ from 'lodash';

import ModalV2 from '@atoms/ModalV2';
import { platformMapper, socialMediaCategoryMapper, SocialMediaLink } from '@services/types/api';
import { Button, TextField } from '@atoms';
import { FIELDS, transformAutoSubmissionData } from '../utils/auto-submission';
import { textFieldStyles } from './styles';
import { LoadingButton } from '@mui/lab';
import { errorMessage, successMessage } from '@services';
import { useEnforceSocialMedia } from '../api/enforce-social-media';
import { useParams } from 'react-router-dom';

interface IAutoSubmissionModalProps {
  open: boolean;
  links: SocialMediaLink[];
  isEnforcing: boolean;
  onClose: () => void;
  onEnforce: () => void;
  onSuccess: () => void;
}

const buttonStylesModal = {
  height: 40,
};

const AutoSubmissionModal: React.FC<IAutoSubmissionModalProps> = (props) => {
  const { open, links, isEnforcing, onClose, onEnforce, onSuccess } = props;

  const { assetId } = useParams();

  const currentCategory = links[0]?.category as keyof typeof autoSubmissionSchema;
  const currentPlatform = links[0]
    ?.platform as keyof (typeof autoSubmissionSchema)[keyof typeof autoSubmissionSchema];

  const currentSchema = autoSubmissionSchema[currentCategory]?.[currentPlatform];

  const fields = FIELDS[currentCategory]?.[currentPlatform] || [];

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<yup.InferType<typeof currentSchema>>({
    resolver: yupResolver(currentSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    reset({
      report_content: links.map((link) => link.mainUrl).join(','),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links]);

  const downloadPreviewImage = async (imageUrl: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${currentCategory}-${currentPlatform}-${Date.now()}-preview.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(error);
    }
  };

  const enforceSocialMediaMutation = useEnforceSocialMedia();

  const { mutate: submitAutoSubmissionMutation, isLoading } = useSubmitAutoSubmission({
    mutationConfig: {
      onSuccess: (res) => {
        if (res?.data?.status === 'submitted') {
          if (process.env.REACT_APP_ENV === 'dev') {
            downloadPreviewImage(res.data.screenshot_url);
          }

          if (assetId) {
            const ids = links.map((link) => link.id);
            enforceSocialMediaMutation.mutate({ data: { ids }, modelId: assetId });
          }

          successMessage('Auto submission has been submitted successfully!');
          reset();
          onClose();
          onSuccess();
        }
      },
      onError: () => {
        errorMessage('Failed to submit auto submission');
      },
    },
  });

  const onCloseModal = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: any) => {
    const transformedData = transformAutoSubmissionData(currentCategory, currentPlatform, data);

    const formData = new FormData();
    Object.entries(transformedData).forEach(([key, value]) => {
      if (value instanceof File || value instanceof Blob) {
        formData.append(key, value);
      } else if (!_.isNull(value) && !_.isUndefined(value)) {
        formData.append(key, String(value));
      }
    });

    submitAutoSubmissionMutation({
      category: currentCategory as any,
      platform: currentPlatform as any,
      data: formData,
    });
  };

  const _renderFields = () => {
    return (
      <>
        {fields.map((field) => {
          switch (field.type) {
            case 'text':
            case 'textarea':
              return (
                <TextField
                  {...(field.type === 'textarea' ? { multiline: true, rows: 1 } : {})}
                  name={field.name as any}
                  label={field.label}
                  defaultValue=""
                  placeholder={field.placeholder}
                  control={control}
                  required={field.required}
                  fullWidth
                  sx={{
                    ...textFieldStyles,
                    '& .MuiOutlinedInput-root': {
                      padding: 0,
                    },
                    maxWidth: 'initial',
                  }}
                />
              );
            case 'file':
              return (
                <Controller
                  name={field.name as any}
                  control={control}
                  render={({ field: renderField }) => (
                    <Box
                      width="100%"
                      sx={{
                        ...textFieldStyles,
                        maxWidth: 'initial',
                      }}
                    >
                      <InputLabel htmlFor={field.name}>
                        {field.label}
                        {field.required && <span>&nbsp;*</span>}
                      </InputLabel>
                      <input
                        type="file"
                        id="authorization-file"
                        multiple={false}
                        onChange={(e) => renderField.onChange(e.target.files?.[0])}
                      />
                      {errors[field.name as keyof typeof errors] && (
                        <p
                          style={{
                            color: '#FF4848',
                            fontSize: '12px',
                            lineHeight: 1.66,
                            fontWeight: 400,
                            marginTop: '3px',
                            marginBottom: 0,
                          }}
                        >
                          {errors[field.name as keyof typeof errors]?.message}
                        </p>
                      )}
                    </Box>
                  )}
                />
              );
            default:
              return null;
          }
        })}
      </>
    );
  };

  return (
    <ModalV2
      open={open}
      onClose={onCloseModal}
      title="Auto Submission"
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '600px',
          borderRadius: '24px',
        },
      }}
    >
      <Typography
        variant="h6"
        sx={{ mb: 2, fontWeight: 'bold', fontSize: '20px', lineHeight: '24px' }}
      >
        Platform:{' '}
        <Typography component="span" sx={{ fontSize: '16px', lineHeight: '20px' }}>
          {currentPlatform ? platformMapper[currentPlatform] : ''}
        </Typography>
        {' - '}
        Category:{' '}
        <Typography component="span" sx={{ fontSize: '16px', lineHeight: '20px' }}>
          {currentCategory ? socialMediaCategoryMapper[currentCategory] : ''}
        </Typography>
      </Typography>
      <Box sx={{ width: '100%' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="8px">{_renderFields()}</Stack>
          <Box display="flex" justifyContent="flex-end" gap="8px" mt={4.75}>
            <Button
              sx={{ ...buttonStylesModal }}
              onClick={onCloseModal}
              type="button"
              variant="text"
              color="secondary"
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              loading={isLoading}
              disabled={isEnforcing}
              variant="contained"
              color="primary"
              sx={{ ...buttonStylesModal }}
            >
              Save
            </LoadingButton>
            <LoadingButton
              loading={isEnforcing}
              variant="contained"
              color="primary"
              sx={{ ...buttonStylesModal }}
              onClick={onEnforce}
              disabled={isLoading}
            >
              Manual Submit
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </ModalV2>
  );
};

export default AutoSubmissionModal;
